import axios from "axios";

/**
 * Vuex Actions for Notification Module
 * Asynchronous operations for notification management including API calls,
 * state updates, and complex business logic
 */

/**
 * Generic notification fetch utility
 * Used by other actions for API communication
 * @param {Object} context - Vuex context { commit, state, dispatch, getters }
 * @param {Object} params - Query parameters for the request
 * @returns {Object} Response data or error information
 */
export async function getNotifications({ commit }, params) {
  try {
    const { data } = await axios.get("/notification", {
      params: params,
    });
    return {
      error: false,
      data: data.data,
      hasMore: data.pagination.hasMore,
    };
  } catch (e) {
    return {
      error: true,
      message: e.message || "Error fetching notifications",
    };
  }
}

/**
 * Load notifications with pagination and filtering
 * Primary action for initial notification loading
 * @param {Object} context - Vuex context
 * @param {Object} options - Loading options
 * @param {number} options.page - Page number to load
 * @param {string} options.filter - Filter type: 'all' or 'unread'
 */
export async function loadNotifications(
  { commit, state, dispatch },
  { page = 1, filter = state.filter } = {}
) {
  commit("SET_LOADING", true);
  commit("SET_FILTER", filter);

  try {
    const response = await axios.get("/notification", {
      params: {
        page,
        limit: state.limit,
        filter,
      },
    });

    if (response.data.success) {
      commit("SET_NOTIFICATIONS", response.data.data);
      commit("SET_HAS_MORE", response.data.pagination.hasMore);
      commit("SET_CURRENT_PAGE", page);
      console.log(response.data.data);

      // Load unread count after notifications
      await dispatch("fetchUnreadCount");
    }
    return response.data;
  } catch (error) {
    console.error("Error loading notifications:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

/**
 * Load more notifications for pagination
 * Appends additional notifications to existing list
 * @param {Object} context - Vuex context
 */
export async function loadMoreNotifications({ commit, state, dispatch }) {
  commit("SET_LOADING_MORE", true);
  const nextPage = state.currentPage + 1;

  try {
    const response = await axios.post("/notification/load-more", {
      page: nextPage,
      limit: state.limit,
      filter: state.filter,
    });

    if (response.data.success) {
      commit("APPEND_NOTIFICATIONS", response.data.data);
      commit("SET_HAS_MORE", response.data.pagination.hasMore);
      commit("SET_CURRENT_PAGE", nextPage);
    }
    return response.data;
  } catch (error) {
    console.error("Error loading more notifications:", error);
    throw error;
  } finally {
    commit("SET_LOADING_MORE", false);
  }
}

/**
 * Fetch current unread notification count
 * Updates the unread count badge
 * @param {Object} context - Vuex context
 */
export async function fetchUnreadCount({ commit }) {
  try {
    const response = await axios.get("/notification/unread-count");
    if (response.data.success) {
      commit("SET_UNREAD_COUNT", response.data.data.unreadCount);
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching unread count:", error);
    throw error;
  }
}

/**
 * Mark specific notification as read
 * Updates both local state and server status
 * @param {Object} context - Vuex context
 * @param {string} notificationId - ID of notification to mark as read
 */
export async function markAsRead({ commit, dispatch }, notificationId) {
  try {
    await axios.patch(`/notification/${notificationId}/read`);

    // Update local state immediately for better UX
    commit("MARK_AS_READ", notificationId);

    // Sync with server for accurate count
    await dispatch("fetchUnreadCount");

    return { success: true };
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
}

/**
 * Mark all notifications as read
 * Bulk operation for clearing all unread notifications
 * @param {Object} context - Vuex context
 */
export async function markAllAsRead({ commit, dispatch }) {
  commit("SET_MARKING_ALL_AS_READ", true);

  try {
    const response = await axios.patch("/notification/mark-all-read");

    if (response.data.success) {
      // Update local state immediately
      commit("MARK_ALL_AS_READ");

      // Sync unread count with server
      await dispatch("fetchUnreadCount");
    }
    return response.data;
  } catch (error) {
    console.error("Error marking all as read:", error);
    throw error;
  } finally {
    commit("SET_MARKING_ALL_AS_READ", false);
  }
}

/**
 * Refresh notifications
 * Reloads current notification view with latest data
 * @param {Object} context - Vuex context
 */
export async function refreshNotifications({ dispatch, state }) {
  return await dispatch("loadNotifications", {
    page: 1,
    filter: state.filter,
  });
}

/**
 * Change notification filter
 * Switches between 'all' and 'unread' views
 * @param {Object} context - Vuex context
 * @param {string} filter - New filter to apply
 */
export async function changeFilter({ commit, dispatch }, filter) {
  commit("SET_FILTER", filter);
  return await dispatch("loadNotifications", { page: 1, filter });
}
